import { useState } from 'react';
import config from '@cof/ukweb-config/lib/clientConfig';
import { useAxios, useAxiosCallback } from '../hooks/useAxios';
import getConfig from './config';
import { adobeData } from '../api/client/adobeParams';
import * as promises from '../utilities/promises';
import { AxiosRequestConfig } from 'axios';
import {
    UseAxiosCallbackReturn,
    UseAxiosReturn,
    AddressLookupRequest,
    AddressLookupResponse,
    FormDataJwtPayload,
    FormDataRequestParams,
    FormDataResponse,
    ObCompleteRequest,
    ObCompleteResponse,
    ObStartRequest,
    ObStartResponse,
    QuotationSubmitRequest,
    QuotationSubmitResponse
} from '../types';
import type { AdobeExperimentParams } from '../components/experiments/offers';

const ARTIFICIAL_DELAY = 1500;

export const useFormData = (
    params?: FormDataRequestParams,
    _parsedFormData?: FormDataJwtPayload
): UseAxiosCallbackReturn<FormDataResponse, FormDataRequestParams> =>
    useAxiosCallback<FormDataResponse, FormDataRequestParams>(() => ({ ...getConfig('form'), params }));

export const useObStartData = (data?: ObStartRequest): UseAxiosReturn<ObStartResponse> =>
    useAxios<ObStartResponse>(() => ({ ...getConfig('obStart'), data, method: 'post' }));

export const useObCompleteData = (
    data?: ObCompleteRequest
): UseAxiosCallbackReturn<ObCompleteResponse, ObCompleteRequest> =>
    useAxiosCallback<ObCompleteResponse, ObCompleteRequest>(() => ({
        ...getConfig('obComplete'),
        data,
        method: 'post'
    }));

export const useFormSubmit = (): UseAxiosCallbackReturn<QuotationSubmitResponse, QuotationSubmitRequest> => {
    const [delaying, setDelaying] = useState(false);
    const [submitForm, submitting, ...rest] = useAxiosCallback<QuotationSubmitResponse, QuotationSubmitRequest>(
        (data) => {
            setDelaying(true);
            setTimeout(() => setDelaying(false), ARTIFICIAL_DELAY);
            const { downstreamVersionIsEnabled } = config.get('useDownstreamVersion') || {};
            return withAsyncData(
                {
                    ...getConfig('quotation'),
                    withCredentials: downstreamVersionIsEnabled ? true : undefined,
                    data,
                    method: 'post'
                },
                adobeData()
            );
        }
    );

    return [submitForm, submitting || delaying, ...rest];
};

export const useAddressLookupData = (
    sessionId?: string,
    upstreamTarget?: string
): UseAxiosCallbackReturn<AddressLookupResponse, AddressLookupRequest> =>
    useAxiosCallback<AddressLookupResponse, AddressLookupRequest>((data) => {
        const headers = {
            ...getConfig('addressLookup').headers,
            ...{ 'Session-Id': sessionId, 'X-Upstream-Env': upstreamTarget }
        };
        return { ...getConfig('addressLookup'), headers, data, method: 'post' };
    });

export const shouldShowTechError = (brand: string): boolean => {
    const { brands } = config.get('showTechError');
    return brands.map((x: string) => x.toLowerCase()).includes(brand.toLowerCase());
};

export const withAsyncData = (
    originalPayload: AxiosRequestConfig,
    data: Promise<AdobeExperimentParams | undefined>
): Promise<AxiosRequestConfig> =>
    promises.safe(data).then((p) => ({ ...originalPayload, data: { ...originalPayload.data, ...p } }));
